import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { ClientFormFields } from '@/clients/client-form';
import { useClient, useDeleteClient, useUpdateClient } from '@/clients/queries';
import { ClientUpdateSchema, type ClientUpdateInput } from '@/clients/schemas';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

export const ClientDetailPage = () => {
  const { clientId } = useParams({ from: '/_protected/clients/$clientId' });
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(clientId);
  const updateClient = useUpdateClient(clientId);
  const deleteClient = useDeleteClient();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Загрузка…</div>;
  }
  if (!client) {
    return (
      <div className="p-6 space-y-2">
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Клиенты
        </Link>
        <div className="text-muted-foreground">Клиент не найден.</div>
      </div>
    );
  }

  const isDeleted = Boolean(client.deletedAt);

  const handleUpdate = (values: ClientUpdateInput): void => {
    updateClient.mutate(values, {
      onSuccess: () => {
        toast.success('Клиент обновлён');
        setEditOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const handleDelete = (): void => {
    deleteClient.mutate(client.id, {
      onSuccess: () => {
        toast.success('Клиент удалён');
        setConfirmDeleteOpen(false);
        void navigate({ to: '/clients' });
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Клиенты
      </Link>

      <PageShell
        title={client.name}
        actions={
          isDeleted ? (
            <Badge variant="destructive">Удалён</Badge>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setEditOpen(true);
                }}
              >
                Редактировать
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmDeleteOpen(true);
                }}
              >
                Удалить
              </Button>
            </>
          )
        }
      >
        <dl className="grid grid-cols-[200px_1fr] gap-y-3 p-6 text-sm">
          <dt className="text-muted-foreground">ID</dt>
          <dd className="font-mono text-xs">{client.id}</dd>
          <dt className="text-muted-foreground">Описание</dt>
          <dd>{client.description ?? '—'}</dd>
          <dt className="text-muted-foreground">Тип</dt>
          <dd>{client.isInternal ? <Badge variant="secondary">internal</Badge> : 'external'}</dd>
          <dt className="text-muted-foreground">Создан</dt>
          <dd>{formatDate(client.createdAt)}</dd>
          <dt className="text-muted-foreground">Обновлён</dt>
          <dd>{formatDate(client.updatedAt)}</dd>
          {client.deletedAt ? (
            <>
              <dt className="text-muted-foreground">Удалён</dt>
              <dd>{formatDate(client.deletedAt)}</dd>
            </>
          ) : null}
        </dl>
      </PageShell>

      <FormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Редактирование клиента"
        schema={ClientUpdateSchema}
        defaultValues={{
          name: client.name,
          description: client.description ?? '',
          isInternal: client.isInternal,
        }}
        onSubmit={handleUpdate}
        isPending={updateClient.isPending}
      >
        <ClientFormFields />
      </FormDialog>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Удалить клиента?"
        description={`Клиент «${client.name}» будет soft-deleted. Связанные объекты не удаляются.`}
        onConfirm={handleDelete}
        isPending={deleteClient.isPending}
      />
    </div>
  );
};
